import React from 'react';
import { bearerHeaders } from '../api.ts';
import { api, Mark, MarkHunt, QidUser, MarkChatMessage, MarkShootingPlanItem, MarkRolePermissions } from '../api.ts';
import { resolveUrl } from '../api.ts';
import { apiFetch } from '../appFetch';
import { planSpeciesFamilyKey } from '../swedishHuntingLegality';
import { Camera, Check, FileText, Images, MessageCircle, Radio, Target } from 'lucide-react';
import { RADIUS, TYPE, useTheme } from '../theme';
import { Badge, Button, Card, EmptyState, Field, Icon, Input, Loader, useIsMobile } from '../components/ui';
import { ChatComposer } from '../components/chat/ChatComposer';
import { isDemoMark } from '../views/minjakt/DemoMark';
import { scrollToShowEnd, useBottomInset } from '../components/chat/useBottomInset';
import { chatHuntStatus } from '../components/chat/huntStatus';
import { ReportMenu } from '../components/moderation/ReportMenu';
import { useIntegrationVisibility } from '../lib/integrationVisibility';

interface GruppchatTabProps {
  mark: Mark;
  user: QidUser | null | undefined;
  isAdmin?: boolean;
  myPerms: MarkRolePermissions | null;
  messagesLd: boolean;
  messages: MarkChatMessage[];
  setMessages: React.Dispatch<React.SetStateAction<MarkChatMessage[]>>;
  plan: MarkShootingPlanItem[];
  setPlan: React.Dispatch<React.SetStateAction<MarkShootingPlanItem[]>>;
  planSeason: string;
  setViltRegs: React.Dispatch<React.SetStateAction<any[]>>;
  chatMsg: string;
  setChatMsg: React.Dispatch<React.SetStateAction<string>>;
  chatBusy: boolean;
  setChatBusy: React.Dispatch<React.SetStateAction<boolean>>;
  chatAttachMenu: boolean;
  setChatAttachMenu: React.Dispatch<React.SetStateAction<boolean>>;
  chatEndRef: React.RefObject<HTMLDivElement>;
  chatContainerRef: React.RefObject<HTMLDivElement>;
  chatSyncOpen: 'whatsapp' | 'telegram' | 'sms' | null;
  setChatSyncOpen: React.Dispatch<React.SetStateAction<'whatsapp' | 'telegram' | 'sms' | null>>;
  chatInts: any[];
  setChatInts: React.Dispatch<React.SetStateAction<any[]>>;
  chatWaQR: string | null;
  setChatWaQR: React.Dispatch<React.SetStateAction<string | null>>;
  chatTgToken: string;
  setChatTgToken: React.Dispatch<React.SetStateAction<string>>;
  chatTgChatId: string;
  setChatTgChatId: React.Dispatch<React.SetStateAction<string>>;
  chatTgBusy: boolean;
  setChatTgBusy: React.Dispatch<React.SetStateAction<boolean>>;
  chatWaGroupBusy: boolean;
  setChatWaGroupBusy: React.Dispatch<React.SetStateAction<boolean>>;
  chatWaPhoneInput: string | null;
  setChatWaPhoneInput: React.Dispatch<React.SetStateAction<string | null>>;
  tgBotInfo: { username: string | null; firstName: string | null } | null;
  smsBusy: boolean;
  smsResult: { message: string; success: boolean } | null;
  setSmsResult: React.Dispatch<React.SetStateAction<{ message: string; success: boolean } | null>>;
  joinableHuntMeetingIdVerified: string | null;
  handleJoinFromChat: (meetingId: string) => void;
  loadChatInts: () => Promise<void>;
  startChatWa: () => Promise<void>;
  removeChatWa: () => Promise<void>;
  saveChatTg: () => Promise<void>;
  removeChatTg: () => Promise<void>;
  saveChatSms: () => Promise<void>;
  removeChatSms: () => Promise<void>;
  syncSmsMembrs: () => Promise<void>;
  doUploadFile: (accept: string, msgType: string, useCamera?: boolean) => Promise<void>;
  doSendChat: () => void;
  avatarColor: (email?: string | null) => string;
  CHAT_GAME_ANIMALS: { sv: string; fk: string; kw: string[] }[];
}


// Plattformarnas egna färger används bara som prick i knapparna.
const BRAND = { whatsapp: '#25D366', telegram: '#2AABEE', sms: '#F07800' } as const;

const dayKey = (iso: string) => new Date(iso).toDateString();
function dayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const y = new Date(); y.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Idag';
  if (d.toDateString() === y.toDateString()) return 'Igår';
  return d.toLocaleDateString('sv-SE', { weekday: 'short', day: 'numeric', month: 'short', ...(d.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {}) });
}
const timeLabel = (iso: string) => new Date(iso).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });

/** Samma avsändare inom fem minuter visas utan nytt namn och ny avatar. */
export function continuesGroup(prev: Pick<MarkChatMessage, 'senderEmail' | 'createdAt' | 'messageType'> | undefined, m: Pick<MarkChatMessage, 'senderEmail' | 'createdAt' | 'messageType'>): boolean {
  if (!prev || prev.senderEmail !== m.senderEmail) return false;
  if (dayKey(prev.createdAt) !== dayKey(m.createdAt)) return false;
  if (['bot_info', 'kill_question', 'hunt_started'].includes(prev.messageType || '')) return false;
  return new Date(m.createdAt).getTime() - new Date(prev.createdAt).getTime() < 5 * 60_000;
}

export function GruppchatTab({
  mark, user, isAdmin, messagesLd, messages, setMessages,
  plan, setPlan, planSeason, setViltRegs,
  chatMsg, setChatMsg, chatBusy,
  chatEndRef, chatContainerRef,
  chatSyncOpen, setChatSyncOpen, chatInts,
  chatWaQR, chatTgToken, setChatTgToken, chatTgChatId, setChatTgChatId,
  chatTgBusy, chatWaGroupBusy, setChatWaGroupBusy,
  chatWaPhoneInput, setChatWaPhoneInput,
  tgBotInfo, smsBusy, smsResult,
  handleJoinFromChat, joinableHuntMeetingIdVerified,
  loadChatInts, startChatWa, removeChatWa, saveChatTg, removeChatTg,
  saveChatSms, removeChatSms, syncSmsMembrs, doUploadFile, doSendChat,
  avatarColor,
}: GruppchatTabProps) {
  // IN3: integrationer som plattformsadmin dolt visas inte här; admin ser dem märkta "dold".
  const { shown: showInt, hiddenForUsers } = useIntegrationVisibility();
  const T = useTheme();
  const mob = useIsMobile();
  const inset = useBottomInset();
  const barRef = React.useRef<HTMLDivElement>(null);
  const [barH, setBarH] = React.useState(76);
  const list = Array.isArray(messages) ? messages : [];

  // B7: status för "Jakt startad"-raderna. Hämtas om när chattens jakter eller den pågående jakten ändras
  // (App.tsx frågar activeMarkHunt var 15:e sekund medan chatten är öppen), så avslut syns utan omladdning.
  const huntIds = React.useMemo(() => [...new Set(list.filter(m => m.messageType === 'hunt_started' && m.fileUrl).map(m => m.fileUrl as string))], [list]);
  const huntIdsKey = huntIds.join(',');
  const [hunts, setHunts] = React.useState<MarkHunt[] | null>(null);
  React.useEffect(() => {
    if (!huntIds.length) return;
    let cancelled = false;
    api.markHunts(mark.id, huntIds).then(h => { if (!cancelled) setHunts(h); }).catch(() => { if (!cancelled) setHunts(prev => prev); });
    return () => { cancelled = true; };
  }, [mark.id, huntIdsKey, joinableHuntMeetingIdVerified]); // eslint-disable-line react-hooks/exhaustive-deps

  // Skrivfältets höjd → luft under sista meddelandet.
  React.useLayoutEffect(() => {
    const el = barRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setBarH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [messagesLd]);
  // Sidan scrollar (som WhatsApp): visa senaste meddelandet när nya kommer eller tangentbordet öppnas.
  // UX9: bara så långt som behövs för att det ska synas ovanför skrivfältet (scrollIntoView skrollade ned
  // en bit även när allt redan syntes, och lade sista meddelandet bakom bottenmenyn).
  React.useEffect(() => {
    if (messagesLd) return;
    requestAnimationFrame(() => {
      const end = chatEndRef.current, bar = barRef.current;
      if (!end || !bar) return;
      const dy = scrollToShowEnd(end.getBoundingClientRect().bottom, bar.getBoundingClientRect().top);
      if (dy > 0) window.scrollBy(0, dy);
    });
  }, [messagesLd, list.length, inset, chatEndRef]);

  if (messagesLd) return <Loader text="Laddar meddelanden…" />;

  const syncButton = (key: 'whatsapp' | 'telegram' | 'sms', label: string, on: boolean, onClick: () => void, open: boolean) => (
    <Button key={key} size="sm" variant="secondary" aria-expanded={open} onClick={onClick} style={{ minHeight: 44, background: open ? T.surface2 : undefined }}
      icon={<span aria-hidden style={{ width: 8, height: 8, borderRadius: '50%', background: on ? BRAND[key] : T.borderHover, flexShrink: 0 }} />}
      iconRight={on ? <Icon as={Check} size={16} color={T.ok} /> : undefined}>
      {label}{hiddenForUsers(key) && <span style={{ ...TYPE.small, color: T.textMuted }}> · dold</span>}
    </Button>
  );
  const help: React.CSSProperties = { color: T.textSub, fontSize: 14, lineHeight: 1.6, margin: 0 };
  const steps = (items: React.ReactNode[]) => <ol style={{ ...help, paddingLeft: 20, margin: '6px 0 10px' }}>{items.map((x, i) => <li key={i}>{x}</li>)}</ol>;
  const note = (ok: boolean, text: string) => <div role="status" style={{ marginTop: 8, padding: '8px 12px', borderRadius: RADIUS.md, background: ok ? T.okSoft : T.dangerSoft, color: ok ? T.ok : T.danger, fontSize: 14, fontWeight: 600 }}>{text}</div>;
  // Som tidigare körs knapparna i synkpanelerna på pointerdown (pålitligare i iOS-appen); tangentbordet ger click med detail 0.
  const tap = (fn: () => unknown) => {
    const run = () => { (async () => { try { await fn(); } catch (err) { console.error(err); } })(); };
    return {
      onPointerDown: (e: React.PointerEvent) => { e.stopPropagation(); run(); },
      onClick: (e: React.MouseEvent) => { e.stopPropagation(); if (e.detail === 0) run(); },
      style: { touchAction: 'manipulation' as const },
    };
  };

  const wa = chatInts.find((i: any) => i.platform === 'whatsapp');
  const tg = chatInts.find((i: any) => i.platform === 'telegram');
  const sms = chatInts.find((i: any) => i.platform === 'sms');

  const tgBot = tgBotInfo?.username && (
    <div style={{ background: T.infoSoft, borderRadius: RADIUS.md, padding: '10px 14px', marginBottom: 12 }}>
      <div style={{ fontWeight: 700, color: T.info, marginBottom: 4 }}>Bjud in rätt bot i din Telegram-grupp:</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: T.text }}>@{tgBotInfo.username}</div>
      <div style={{ ...TYPE.small, color: T.textMuted, marginTop: 4 }}>Sök efter användarnamnet i Telegram och lägg till boten i gruppen.</div>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <h2 style={{ ...TYPE.section, color: T.text, margin: 0 }}>Gruppchatt</h2>
        {isAdmin && <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {showInt('whatsapp') && syncButton('whatsapp', wa?.connected ? (wa?.chat_id ? 'WhatsApp' : 'Anslut WA-grupp') : 'WhatsApp', !!wa?.connected, () => {
            if (chatWaPhoneInput !== null) { setChatWaPhoneInput(null); }
            else { setChatSyncOpen(null); setChatWaPhoneInput(''); }
          }, chatWaPhoneInput !== null)}
          {showInt('telegram') && syncButton('telegram', 'Telegram', !!tg?.connected, () => setChatSyncOpen(chatSyncOpen === 'telegram' ? null : 'telegram'), chatSyncOpen === 'telegram')}
          {showInt('sms') && syncButton('sms', 'SMS', !!sms?.connected, () => setChatSyncOpen(chatSyncOpen === 'sms' ? null : 'sms'), chatSyncOpen === 'sms')}
        </div>}
      </div>

      {/* Synk-paneler */}
      {chatSyncOpen && isAdmin && showInt(chatSyncOpen) && <Card style={{ marginBottom: 12 }}>
        {chatSyncOpen === 'whatsapp' && (wa?.connected ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Badge tone="ok" dot>WhatsApp ansluten</Badge>
            <span style={{ ...TYPE.small, color: T.textMuted }}>Grupp: {mark.name}</span>
            <Button variant="danger" onClick={removeChatWa} style={{ marginLeft: 'auto' }}>Koppla bort</Button>
          </div>
        ) : (
          <div>
            <div style={{ color: T.text, fontWeight: 700, fontSize: 15 }}>Hur ansluter du WhatsApp?</div>
            {steps(['Öppna WhatsApp på din telefon.', <>Gå till <strong>Inställningar → Länkade enheter</strong>.</>, <>Välj <strong>Länka en enhet</strong> och skanna QR-koden nedan.</>, <>Gruppen <strong>{mark.name}</strong> kopplas automatiskt.</>])}
            <p style={{ ...TYPE.small, color: T.textMuted, margin: '0 0 12px' }}>Du behöver redan vara med i en WhatsApp-grupp som heter {mark.name}. Annars skapar du den i WhatsApp först.</p>
            {chatWaQR ? (
              <div style={{ textAlign: 'center' }}>
                <img src={chatWaQR} alt="WhatsApp QR-kod" style={{ width: 240, height: 240, imageRendering: 'pixelated', borderRadius: RADIUS.sm, border: '8px solid #fff', display: 'block', margin: '0 auto 8px' }} />
                <div style={{ ...TYPE.small, color: T.textMuted }}>Uppdateras automatiskt…</div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <Loader inline text="Ansluter till WhatsApp…" />
                <Button variant="secondary" onClick={startChatWa}>Starta om</Button>
              </div>
            )}
          </div>
        ))}
        {chatSyncOpen === 'telegram' && (() => {
          const tgReady = tg?.connected && tg?.chat_id; const tgPending = tg?.connected && !tg?.chat_id;
          if (tgReady) return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Badge tone="ok" dot>Telegram ansluten</Badge>
              <span style={{ ...TYPE.small, color: T.textMuted }}>Chat-ID: {tg.chat_id}</span>
              <Button variant="danger" {...tap(removeChatTg)} style={{ ...tap(removeChatTg).style, marginLeft: 'auto' }}>Koppla bort</Button>
            </div>
          );
          if (tgPending) return (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                <Badge tone="info" dot>Bot ansluten</Badge>
                <Badge tone="accent">Väntar på aktivering</Badge>
              </div>
              {tgBot}
              <div style={{ color: T.text, fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Sista steget – aktivera synken</div>
              <p style={help}>Boten är tillagd men vet inte vilken grupp den ska lyssna på. <strong style={{ color: T.text }}>Skicka valfritt meddelande i Telegram-gruppen</strong>, så hittar boten gruppen själv.</p>
              <p style={{ ...help, marginTop: 8, padding: '8px 12px', background: T.surface2, borderRadius: RADIUS.md }}>Svarar inte boten? Öppna @BotFather → /setprivacy → din bot → Disable och skicka meddelandet igen.</p>
              <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', position: 'relative', zIndex: 10 }}>
                <Button {...tap(loadChatInts)}>Uppdatera status</Button>
                <Button variant="danger" {...tap(removeChatTg)}>Koppla bort</Button>
              </div>
            </div>
          );
          return (
            <div>
              {tgBot}
              <div style={{ color: T.text, fontWeight: 700, fontSize: 15 }}>Hur ansluter du Telegram?</div>
              {steps([<>Öppna <strong>@BotFather</strong> i Telegram och skriv <strong>/newbot</strong>.</>, <>Följ instruktionerna och kopiera <strong>bot-token</strong>.</>, <>Lägg till boten som <strong>admin</strong> i Telegram-gruppen <strong>{mark.name}</strong>.</>, <>Klistra in bot-token nedan och välj <strong>Anslut</strong>.</>, <>Skicka ett meddelande i Telegram-gruppen – chat-ID hittas automatiskt.</>])}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 12, marginBottom: 12 }}>
                <Field label="Bot-token"><Input value={chatTgToken} onChange={e => setChatTgToken(e.target.value)} placeholder="1234567890:AAF..." /></Field>
                <Field label="Chat-ID" hint="Valfritt – boten hittar det själv."><Input value={chatTgChatId} onChange={e => setChatTgChatId(e.target.value)} placeholder="-100123456789" /></Field>
              </div>
              <Button onClick={saveChatTg} disabled={!chatTgToken} loading={chatTgBusy}>{chatTgBusy ? 'Sparar…' : 'Anslut'}</Button>
            </div>
          );
        })()}
        {chatSyncOpen === 'sms' && (sms?.connected ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
              <Badge tone="ok" dot>SMS (Supertext) anslutet</Badge>
              <span style={{ ...TYPE.small, color: T.textMuted }}>Grupp: {mark.name}</span>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button {...tap(syncSmsMembrs)} loading={smsBusy}>{smsBusy ? 'Synkar…' : 'Synka medlemmar'}</Button>
              <Button variant="danger" {...tap(removeChatSms)}>Koppla bort</Button>
            </div>
            {smsResult && note(smsResult.success, smsResult.message)}
          </div>
        ) : (
          <div>
            <div style={{ color: T.text, fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Anslut Supertext SMS-grupp</div>
            <p style={{ ...help, marginBottom: 12 }}>Meddelanden i gruppchatten skickas automatiskt som SMS till alla gruppmedlemmar med telefonnummer.</p>
            {smsResult && <div style={{ marginBottom: 8 }}>{note(smsResult.success, smsResult.message)}</div>}
            <Button {...tap(saveChatSms)} loading={smsBusy}>{smsBusy ? 'Skapar grupp…' : 'Anslut SMS'}</Button>
          </div>
        ))}
      </Card>}

      {/* WhatsApp: inbjudningslänk */}
      {chatWaPhoneInput !== null && showInt('whatsapp') && <Card style={{ marginBottom: 12 }}>
        <div style={{ color: T.text, fontWeight: 700, fontSize: 15 }}>Anslut WhatsApp-grupp: {mark.name}</div>
        {steps([<>Öppna <strong>WhatsApp</strong> på din telefon.</>, <>Skapa en grupp (<strong>{mark.name}</strong>) eller öppna en befintlig.</>, <>Gå till <strong>Gruppinfo → Bjud in via länk → Kopiera länk</strong>.</>, <>Klistra in länken nedan (<em>chat.whatsapp.com/XXXXX</em>).</>])}
        <p style={{ ...TYPE.small, color: T.textMuted, margin: '0 0 12px' }}>Därefter synkas meddelanden i realtid mellan Qvenari och WhatsApp-gruppen.</p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Field label="Inbjudningslänk" style={{ flex: '1 1 240px' }}>
            <Input value={chatWaPhoneInput} onChange={e => setChatWaPhoneInput(e.target.value)} placeholder="https://chat.whatsapp.com/XXXXXXXX" autoFocus />
          </Field>
          <Button disabled={!chatWaPhoneInput?.trim()} loading={chatWaGroupBusy} onClick={async () => {
            if (!chatWaPhoneInput?.trim()) return;
            setChatWaGroupBusy(true);
            const chatSyncHInner = { ...bearerHeaders(localStorage.getItem('qvenari_access_token') || ''), 'X-Api-Key': 'dev-secret', 'Content-Type': 'application/json' };
            try {
              const r = await apiFetch(`/api/integrations/${mark.id}/whatsapp/create-group`, { method: 'POST', headers: chatSyncHInner, body: JSON.stringify({ inviteLink: chatWaPhoneInput.trim() }) });
              const d = await r.json();
              if (d.success) {
                await loadChatInts();
                setChatWaPhoneInput(null);
                setChatSyncOpen(null);
                alert('WhatsApp-grupp ansluten! Meddelanden synkas nu.');
              } else {
                alert('Fel: ' + (d.error || 'Kunde inte ansluta till grupp'));
              }
            } catch (e: any) { alert('Fel: ' + e.message); } finally { setChatWaGroupBusy(false); }
          }}>
            {chatWaGroupBusy ? 'Ansluter…' : 'Anslut'}
          </Button>
          <Button variant="secondary" onClick={() => setChatWaPhoneInput(null)}>Avbryt</Button>
        </div>
      </Card>}

      {/* Meddelanden: sidan scrollar, skrivfältet ligger fast längst ner. */}
      <div ref={chatContainerRef} role="log" aria-label="Meddelanden" style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingBottom: barH + inset + 12 }}>
        {list.length === 0 && (
          <Card><EmptyState icon={MessageCircle} title="Inga meddelanden ännu" sub="Skriv det första! Bilder och video från jakterna och åtelkamerorna hamnar också här." /></Card>
        )}
        {list.map((m, i) => {
          const prev = list[i - 1];
          const newDay = !prev || dayKey(prev.createdAt) !== dayKey(m.createdAt);
          const own = !!user?.email && m.senderEmail === user.email;
          const cont = !newDay && continuesGroup(prev, m);
          const sep = newDay && (
            <div key={`d-${m.id}`} style={{ display: 'flex', justifyContent: 'center', margin: '14px 0 8px' }}>
              <span style={{ ...TYPE.small, fontSize: 12, fontWeight: 700, color: T.textSub, background: T.surface2, borderRadius: RADIUS.pill, padding: '4px 12px' }}>{dayLabel(m.createdAt)}</span>
            </div>
          );

          if (m.messageType === 'kill_question') {
            let qdata: { family: string; familyKey: string; subcats: string[] } | null = null;
            try { qdata = JSON.parse(m.message || '{}'); } catch { }
            if (!qdata) return sep || null;
            return (
              <React.Fragment key={m.id}>
                {sep}
                <SystemCard T={T} icon={<Icon as={Target} size={18} />} title={`Fällt ${qdata.family} – vilken typ?`}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {qdata.subcats.map((subcat: string) => (
                      <Button key={subcat} size="sm" variant="secondary" style={{ minHeight: 44 }} onClick={async () => {
                        setMessages(prev => prev.filter(msg => msg.id !== m.id));
                        const planRow = plan.find(p => p.gameType === subcat) || plan.find(p => planSpeciesFamilyKey(p.gameType) === qdata!.familyKey);
                        if (planRow) {
                          const newActual = (planRow.actualCount || 0) + 1;
                          await api.upsertMarkShootingPlan({ markId: mark.id, season: planRow.season, gameType: planRow.gameType, plannedCount: planRow.plannedCount, actualCount: newActual, costPerAnimal: planRow.costPerAnimal, notes: `Uppdaterad från chatt ${new Date().toLocaleDateString('sv-SE')} (+1 ${subcat})` }).catch(() => { });
                          api.markShootingPlan(mark.id).then(d => setPlan(d)).catch(() => { });
                        }
                        const familyName = subcat.split(' — ')[0];
                        await api.addViltRegistration({ markId: mark.id, season: planSeason, gameType: familyName, shotDate: new Date().toISOString().slice(0, 10) }).catch(() => { });
                        api.markViltRegistrations(mark.id).then(d => setViltRegs(d)).catch(() => { });
                        const confirmMsg: MarkChatMessage = { id: `bot_ok_${Date.now()}`, markId: mark.id, senderEmail: 'system@qvenari.com', senderName: 'Qvenari', message: `${subcat} registrerad. Avskjutningsplanen uppdaterad.`, messageType: 'bot_info', imageUrl: null as any, fileUrl: null as any, fileName: null as any, fileType: null as any, createdAt: new Date().toISOString() };
                        setMessages(prev => [...prev, confirmMsg]);
                      }}>
                        {subcat.split(' — ')[1] || subcat}
                      </Button>
                    ))}
                    <Button size="sm" variant="ghost" style={{ minHeight: 44 }} onClick={() => setMessages(prev => prev.filter(msg => msg.id !== m.id))}>Avbryt</Button>
                  </div>
                </SystemCard>
              </React.Fragment>
            );
          }
          if (m.messageType === 'bot_info') {
            return (
              <React.Fragment key={m.id}>
                {sep}
                <div style={{ display: 'flex', justifyContent: 'center', margin: '6px 0' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: T.okSoft, color: T.ok, borderRadius: RADIUS.pill, padding: '6px 14px', fontSize: 14, fontWeight: 600, maxWidth: '90%' }}>
                    <Icon as={Check} size={16} />{(m.message || '').replace(/^✅\s*/, '')}
                  </span>
                </div>
              </React.Fragment>
            );
          }
          if (m.messageType === 'hunt_started') {
            // Kompakt anslutningsrad (C1): status, text och knapp på en rad, ingen bild.
            // B7: Anslut bara medan jakten pågår; därefter "Jakten avslutad kl …".
            const hs = m.fileUrl ? chatHuntStatus(m.fileUrl, joinableHuntMeetingIdVerified, hunts) : { state: 'unknown' as const };
            return (
              <React.Fragment key={m.id}>
                {sep}
                <div style={{ display: 'flex', justifyContent: 'center', margin: '6px 0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: 'min(520px, 100%)', padding: '6px 6px 6px 12px', background: T.accentSoft, borderRadius: RADIUS.lg }}>
                    <span aria-hidden style={{ color: T.accentText, display: 'flex' }}><Icon as={Radio} size={18} /></span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Jakt startad · {timeLabel(m.createdAt)}</div>
                      {hs.state === 'ended' && <div style={{ ...TYPE.small, color: T.textSub }}>{hs.endedAt != null ? `Jakten avslutad kl ${timeLabel(new Date(hs.endedAt).toISOString())}` : 'Jakten avslutad'}</div>}
                      {m.message && <div style={{ ...TYPE.small, color: T.textSub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.message}</div>}
                    </div>
                    {hs.state === 'active' && <Button size="sm" style={{ minHeight: 44, flexShrink: 0 }} onClick={() => handleJoinFromChat(m.fileUrl!)}>Anslut</Button>}
                  </div>
                </div>
              </React.Fragment>
            );
          }

          const typeBadge = m.messageType === 'trail_cam' ? <Badge tone="info">Åtelkamera</Badge>
            : m.messageType === 'video' ? <Badge>Video</Badge>
            : m.messageType === 'file' ? <Badge>Fil</Badge> : null;
          return (
            <React.Fragment key={m.id}>
              {sep}
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', justifyContent: own ? 'flex-end' : 'flex-start', marginTop: cont ? 2 : 10 }}>
                {!own && (cont
                  ? <span aria-hidden style={{ width: 32, flexShrink: 0 }} />
                  : <span aria-hidden style={{ width: 32, height: 32, borderRadius: '50%', background: avatarColor(m.senderEmail), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0, alignSelf: 'flex-start' }}>{(m.senderName || '?').charAt(0).toUpperCase()}</span>)}
                <div style={{
                  maxWidth: mob ? '82%' : 'min(560px, 72%)', minWidth: 0, padding: '8px 12px 6px',
                  background: own ? T.accentSoft : T.surface, border: own ? 'none' : `1px solid ${T.border}`,
                  borderRadius: 16, borderTopLeftRadius: !own && !cont ? 4 : 16, borderTopRightRadius: own && !cont ? 4 : 16,
                }}>
                  {!own && !cont && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                      <span style={{ color: T.textSub, fontWeight: 700, fontSize: 13 }}>{m.senderName || 'Okänd'}</span>
                      {typeBadge}
                    </div>
                  )}
                  {m.message && <div style={{ color: T.text, fontSize: 15, lineHeight: 1.45, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{m.message}</div>}
                  {m.imageUrl && <img src={resolveUrl(m.imageUrl)} alt="Bild" style={{ display: 'block', maxWidth: '100%', width: 280, maxHeight: 220, borderRadius: RADIUS.md, marginTop: 4, objectFit: 'cover', cursor: 'pointer' }} onClick={() => window.open(resolveUrl(m.imageUrl!), '_blank')} onError={(e) => { console.error('[Chat] Image load failed:', m.imageUrl); const el = e.currentTarget as HTMLImageElement; el.style.cssText = `display:block;padding:8px;background:${T.surface2};color:${T.textMuted};font-size:13px;border-radius:8px;margin-top:4px;`; el.alt = 'Bilden kunde inte visas'; }} />}
                  {m.fileUrl && m.fileType === 'video' && <video src={m.fileUrl} controls style={{ display: 'block', maxWidth: '100%', width: 320, maxHeight: 240, borderRadius: RADIUS.md, marginTop: 4 }} />}
                  {m.fileUrl && m.fileType !== 'video' && <a href={m.fileUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 4, minHeight: 44, padding: '0 12px', background: T.surface2, borderRadius: RADIUS.md, color: T.text, textDecoration: 'none', fontSize: 14, fontWeight: 600, maxWidth: '100%' }}>
                    <Icon as={FileText} size={18} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.fileName || 'Fil'}</span>
                  </a>}
                  <div style={{ fontSize: 12, color: T.textMuted, textAlign: 'right', marginTop: 2 }}>{timeLabel(m.createdAt)}</div>
                </div>
                {/* AS1c: rapportera/blockera andras meddelanden (inte systemets eller demomarkens). */}
                {!own && !isDemoMark(mark) && m.senderEmail && !m.senderEmail.startsWith('system@') && !m.id.startsWith('bot_') && (
                  <span style={{ alignSelf: 'center', flexShrink: 0 }}>
                    <ReportMenu target={{ type: 'chat', id: m.id }} ownerName={m.senderName} noun="meddelandet"
                      onBlocked={email => setMessages(prev => prev.filter(x => x.senderEmail.toLowerCase() !== email))} />
                  </span>
                )}
              </div>
            </React.Fragment>
          );
        })}
        <div ref={chatEndRef} />
      </div>

      {/* Skrivfältet: fast längst ner, ovanför tangentbordet — och ovanför bottenmenyn (z 950), som annars täckte det. */}
      <div ref={barRef} style={{
        position: 'fixed', left: 0, right: 0, bottom: inset || 'var(--qv-bottom-nav, 0px)', zIndex: 400,
        background: T.surface, borderTop: `1px solid ${T.border}`, boxShadow: T.shadow.sheet,
        paddingBottom: inset ? 0 : 'max(0px, calc(env(safe-area-inset-bottom, 0px) - var(--qv-bottom-nav, 0px)))',
      }}>
        <div style={{ maxWidth: 1264, margin: '0 auto', padding: mob ? '8px 8px' : '10px clamp(16px,3vw,32px)' }}>
          {isDemoMark(mark) ? (
            <div role="note" style={{ ...TYPE.small, color: T.textMuted, padding: '10px 4px', textAlign: 'center' }}>Demomarkens chatt går bara att läsa.</div>
          ) : <ChatComposer
            value={chatMsg} onChange={setChatMsg} onSend={doSendChat} busy={chatBusy}
            placeholder="Skriv ett meddelande"
            attachments={[
              { key: 'camera', label: 'Kamera', icon: <Icon as={Camera} />, onSelect: () => doUploadFile('image/*', 'image', true) },
              { key: 'media', label: 'Bilder och video', icon: <Icon as={Images} />, onSelect: () => doUploadFile('image/*,video/*', 'image') },
              { key: 'file', label: 'Filer', icon: <Icon as={FileText} />, onSelect: () => doUploadFile('.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip,.gpx,.kml', 'file') },
            ]}
          />}
          {!mob && !isDemoMark(mark) && <div style={{ ...TYPE.small, fontSize: 12, color: T.textMuted, margin: '6px 4px 0' }}>Tips: skriv JAKTFRÅGA och datum/tid (t.ex. tisdag 3 mars kl 17) för att boka jakttid i kalendern.{showInt('telegram') && ` Fungerar också från ${showInt('whatsapp') ? 'Telegram och WhatsApp' : 'Telegram'}.`}</div>}
        </div>
      </div>
    </div>
  );
}

/** Systemkort mitt i chatten (t.ex. frågan om fällt vilt). */
function SystemCard({ T, icon, title, children }: { T: ReturnType<typeof useTheme>; icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', margin: '8px 0' }}>
      <div style={{ width: 'min(520px, 100%)', background: T.surface, border: `1px solid ${T.border}`, borderRadius: RADIUS.lg, padding: '12px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: T.text, fontWeight: 700, fontSize: 15, marginBottom: 8 }}>
          <span aria-hidden style={{ color: T.accentText, display: 'flex' }}>{icon}</span>{title}
        </div>
        {children}
      </div>
    </div>
  );
}
